import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import FieldAgrilyticsToolbar from './FieldAgrilyticsToolbar';
import { Map } from './Map';
import { selectors, actions } from '../../store/dux/field-agrilytics-dux';
import { selectors as fieldSelectors } from '../../store/dux/fields-dux';
import SelectAField from './SelectAField';
import ScopedMapControls from './ScopedMapControls';

MapScope.propTypes = {
  mapScope: PropTypes.string,
};

export default function MapScope(props) {
  const { mapScope = 'default' } = props;
  const dispatch = useDispatch();

  const [filterDate, setFilterDate] = useState(new Date().toISOString());
  const fieldRevisions = useSelector(selectors.fieldRevisions(mapScope));
  const selectedField = useSelector(selectors.selectedField(mapScope));
  const selectedRevision = useSelector(selectors.selectedRevision(mapScope));
  const fields = useSelector(fieldSelectors.growerFields);

  const endsAt = useMemo(
    () =>
      selectedRevision?.endsAt ? new Date(selectedRevision.endsAt) : new Date(),
    [selectedRevision],
  );

  const boundaryFilters = useMemo(
    () => ({
      boundary: selectedRevision?.shape,
      boundaryId: selectedRevision?.fieldBoundaryId,
    }),
    [selectedRevision],
  );

  const setSelectedField = useCallback(setSelectedFieldCallback, [
    dispatch,
    mapScope,
  ]);

  const setSelectedRevision = useCallback(setSelectedRevisionCallback, [
    dispatch,
    mapScope,
  ]);

  const loadRevisions = useCallback(loadRevisionsCallback, [
    dispatch,
    mapScope,
  ]);

  useEffect(loadFieldsEffect, [
    dispatch,
    fields,
    setSelectedField,
    setSelectedRevision,
  ]);

  useEffect(loadRevisionsEffect, [
    selectedField,
    setSelectedField,
    loadRevisions,
    dispatch,
  ]);

  useEffect(revisionsLoadedEffect, [
    dispatch,
    fieldRevisions,
    selectedRevision,
    setSelectedRevision,
  ]);

  useEffect(() => {
    setFilterDate(endsAt);
  }, [endsAt]);

  function setSelectedFieldCallback(aaid) {
    dispatch(actions.setSelectedField(aaid, mapScope));
  }

  function setSelectedRevisionCallback(revision) {
    dispatch(actions.setSelectedRevision(revision, mapScope));
  }

  function loadRevisionsCallback(aaid) {
    dispatch(actions.loadFieldRevisions(aaid, mapScope));
  }

  function loadFieldsEffect() {
    if (!fields.length) {
      setSelectedField(null);
      setSelectedRevision(null);
      dispatch(actions.resetFieldRevisions());
    }
  }

  function loadRevisionsEffect() {
    if (!selectedField) return;
    loadRevisions(selectedField);
  }

  function revisionsLoadedEffect() {
    if (!fieldRevisions.length || selectedRevision) return;
    setSelectedRevision(fieldRevisions[0]);
  }

  return (
    <>
      <FieldAgrilyticsToolbar
        mapScope={mapScope}
        fields={fields}
        selectedField={selectedField}
        fieldRevisions={fieldRevisions}
        selectedRevision={selectedRevision}
        onFieldSelection={onFieldSelection}
        onVersionSelection={onVersionSelection}
      />
      {!boundaryFilters?.boundary ? <SelectAField /> : buildMap()}
    </>
  );

  function buildMap() {
    return (
      <Map
        aaid={selectedField}
        mapId={`map-${mapScope}`}
        getControls={() => (
          <ScopedMapControls
            mapScope={mapScope}
            onCalendarDate={handleCalendarDateSelection}
          />
        )}
        endsAt={filterDate}
        {...{ ...boundaryFilters }}
      />
    );
  }

  function onFieldSelection(aaid) {
    setSelectedField(aaid);
  }

  function onVersionSelection(revisionId) {
    const fieldRevision = fieldRevisions.find(
      (revision) => revision.id === parseInt(revisionId, 10),
    );
    setSelectedRevision(fieldRevision);
  }

  function handleCalendarDateSelection(calendarDate) {
    setFilterDate(calendarDate.toISOString());
  }
}
